(ns minorodata.components.connections.overview
  (:require ["lucide-react" :refer [AlertCircle Loader2 PencilIcon Trash2Icon]]
            [minorodata.app.handlers :refer [dispatch dispatch-sync]]
            [minorodata.app.utils :as app.utils]
            [minorodata.components.connections.forms.generator :as gen]
            [minorodata.connections.list :as connections-list]
            [minorodata.utils :as utils :refer [contains-value]]
            [re-posh.core :as re-posh]
            [reagent.core :as reagent]
            [reitit.frontend.easy :refer [href]]
            [ui.button :refer [button]]
            [ui.card :refer [card card-overlay]]
            [ui.chip :refer [chip]]
            [ui.tooltip :refer [tooltip]]
            [ui.typography :as typography]
            [ui.skeleton :refer [skeleton]]))

;; A small block with a logo to be shown on overview screen
(defn connection-preview [{:keys [href image-source name]}]
  [card {:tag :a
         :href href
         :class "group bg-white"}
   [card-overlay]

   [:div {:class "p-2 aspect-video w-9/12 mx-auto text-center"}
    [:img {:src     (app.utils/maybe-local-url image-source)
           :alt     name
           :loading "lazy"
           :class   "h-full max-w-full object-contain align-top"}]]
   [:div {:class "absolute bottom-0 left-0 right-0 p-2"}
    [:div {:class "opacity-0 p-1 rounded-xs bg-kleene-background group-hover:opacity-100 transition-opacity overflow-hidden"}
     [typography/label {:class "inline-blocktruncate"} name]]]])


(defn card-loading [{:keys [loading-message]}]
  [card {:class "bg-white"}
   [:div {:class "flex items-center justify-center gap-4 p-4"
          :style {:height 144}}
    [:> Loader2 {:class "animate-spin"}]
    [typography/label loading-message]]])


(defn warehouse-connector-button [{:keys [warehouse-definition action]}]
  (let [{:keys [name image-source id]} warehouse-definition
        click-handler #(dispatch [:connector/add {:connector/action action
                                                  :connector/type   id}])]
    [:button {:class "relative flex-1 border hover:bg-kleene-hover-background group transition-colors first:rounded-l border-r-0 last:rounded-r last:border-r dark:bg-white/90"}
     [:div {:class "w-10/12 mx-auto p-4"
            :on-click click-handler}
      [:img {:src   (app.utils/maybe-local-url image-source)
             :title name
             :style {:height 78}
             :class "align-top object-contain max-w-full"}]]]))

(defn warehouse-selector [{:keys [dark-mode]}]
  (let [bigquery-available? @(re-posh.core/subscribe [:unleash/is-enabled "bigquery-available"])
        ;; TODO: remove the filtering as soon as bigquery is officially available
        warehouses (cond->> connections-list/warehouses
                     (not bigquery-available?) (remove #(= "bigquery" (:id %))))]
    [card
     [:div {:class "p-4 flex flex-col gap-2"}
      [typography/label "Connect to a Warehouse"]

      (into [:div {:class "flex my-2"}]
            (for [w warehouses]
              [warehouse-connector-button {:warehouse-definition w
                                           :dark-mode            dark-mode
                                           :action               :save-warehouse}]))]]))


(defn warehouses []
  (let [warehouse-status        @(re-posh/subscribe [:warehouse-status/pull])
        {warehouse-type :warehouse-data/sink-type} @(re-posh/subscribe [:warehouse-data/pull])
        warehouse-loading?      @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-warehouse-status])
        warehouse-saving?       @(re-posh/subscribe [:mold-http/loading? :connections.api/save-warehouse])
        warehouse-data-loading? @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-warehouse-data])
        warehouse-id            (:warehouse-status/sink warehouse-status)
        valid?                  (= "ok" (:status (:warehouse-status/sink-status warehouse-status)))
        warehouse-definition    (utils/find-by-field connections-list/warehouses :id warehouse-id)
        edit-warehouse          #(dispatch [:connector/add {:connector/action :save-warehouse
                                                            :connector/type   warehouse-type
                                                            :connector/id     warehouse-id}])
        delete-warehouse        #(dispatch [:connector/add {:connector/action :delete-warehouse
                                                            :connector/type   warehouse-type
                                                            :connector/id     warehouse-id}])

        can-edit?               (or @(re-posh/subscribe [:users.subs/admin?])
                                    @(re-posh/subscribe [:users.subs/superadmin?]))]
    (cond
      (or (and warehouse-loading? (not warehouse-status))
          warehouse-data-loading?
          warehouse-saving?)
      [card-loading {:loading-message "Checking warehouse connection..."}]

      (nil? (:warehouse-status/sink-status warehouse-status))
      [warehouse-selector]

      :else
      [card {:class "dark:bg-white group"}
       [:div {:class "p-8 w-full flex items-center justify-center"}
        [:div
         [:img {:src   (app.utils/maybe-local-url (:image-source warehouse-definition))
                :title (:name warehouse-definition)
                :class "max-w-full mx-auto align-top object-contain"
                :style {:height 80}}]]]

       (when can-edit?
         [:div {:class "absolute top-0 right-0 p-2"}
          [:div {:class "flex gap-1"}
           [button {:variant  :empty-transparent
                    :data-cy  :warehouse-edit-btn
                    :icon     PencilIcon
                    :size     :icon
                    :on-click edit-warehouse}]

           [button {:variant  :empty-transparent
                    :class    "text-kleene-text-error hover:bg-kleene-text-error/10"
                    :data-cy  :warehouse-delete-btn
                    :icon     Trash2Icon
                    :size     :icon
                    :on-click delete-warehouse}]]])

       [:footer {:class "absolute bottom-0 left-0 right-0 p-2"}
        (if can-edit?
          (when-not valid?
            [:div {}
             [tooltip {:content (:error (:warehouse-status/sink-status warehouse-status))}
              [:div
               [chip {:icon [:> AlertCircle {:size 14}]}
                "Broken configuration"]]]])

          [gen/warehouse-status])]])))


(defn store-click-handler [id warehouse-type warehouse-id]
  (fn []
    (let [warehouse @(re-posh/subscribe [:warehouse-data/pull])]
      (dispatch-sync [:warehouse-data/update (assoc-in warehouse [:warehouse-data/json-data :store-type] id)])
      (dispatch [:connector/add
                 {:connector/action :save-warehouse
                  :connector/type   warehouse-type
                  :connector/id     warehouse-id}]))))


(defn filestore-connector-button [{:keys [warehouse-definition]}]
  (let [{:keys [name image-source id warehouse-type warehouse-id]} warehouse-definition
        click-handler (store-click-handler id warehouse-type warehouse-id)]
    [:button {:class "flex-1 border hover:bg-kleene-hover-background transition-colors first:rounded-l border-r-0 last:rounded-r last:border-r dark:bg-white/90"}
     [:div {:class "w-10/12 mx-auto p-4"
            :on-click click-handler}
      [:img {:src   (app.utils/maybe-local-url image-source)
             :title name
             :style {:height 78}
             :class "align-top object-contain max-w-full"}]]]))


(defn filestore-selector [{:keys [classes]}]
  (let [filestores       connections-list/filestores
        warehouse-status @(re-posh/subscribe [:warehouse-status/pull])
        warehouse-id     (:warehouse-status/sink warehouse-status)

        {warehouse-type :warehouse-data/sink-type} @(re-posh/subscribe [:warehouse-data/pull])]
    [card
     [:div {:class "p-4 flex flex-col gap-2"}
      [typography/label "Connect to a Filestore"]

      [:div {:class "flex justify-center-center my-2"}
       [filestore-connector-button {:warehouse-definition (assoc (first filestores)
                                                                 :warehouse-id warehouse-id
                                                                 :warehouse-type warehouse-type)
                                    :action               :save-warehouse
                                    :classes              classes}]
       (when (or (nil? warehouse-type)
                 (= warehouse-type "snowflake"))
         [filestore-connector-button {:warehouse-definition (assoc (second filestores)
                                                                   :warehouse-id warehouse-id
                                                                   :warehouse-type warehouse-type)
                                      :action               :save-warehouse
                                      :classes              classes}])]]]))


(defn filestores []
  (let [filestore-status        @(re-posh/subscribe [:filestore-status/pull])
        filestore-loading?      @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-filestore-status])
        filestore-data-loading? @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-filestore-data])
        warehouse-saving?       @(re-posh/subscribe [:mold-http/loading? :connections.api/save-warehouse])
        warehouse-data-loading? @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-warehouse-data])
        warehouse-loading?      @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-warehouse-status])

        warehouse-status        @(re-posh/subscribe [:warehouse-status/pull])
        warehouse-data          @(re-posh/subscribe [:warehouse-data/pull])

        {warehouse-type :warehouse-data/sink-type} warehouse-data

        filestore-id            (:filestore-status/store filestore-status)
        warehouse-id            (:warehouse-status/sink warehouse-status)

        edit-filestore          #(dispatch [:connector/add {:connector/action :save-warehouse
                                                            :connector/type   warehouse-type
                                                            :connector/id     warehouse-id}])

        valid?                  (= "ok" (:status (:filestore-status/store-status filestore-status)))
        filestore-definition    (utils/find-by-field connections-list/filestores :id filestore-id)

        can-edit?               (or @(re-posh/subscribe [:users.subs/admin?])
                                    @(re-posh/subscribe [:users.subs/superadmin?]))]

    (if (or filestore-loading? warehouse-loading? filestore-data-loading? warehouse-data-loading? warehouse-saving?)
      [card-loading {:loading-message "Checking store connection..."}]

      (when-not (nil? (:warehouse-status/sink-status warehouse-status))
        (if (nil? (:filestore-status/store-status filestore-status))
          [filestore-selector]

          [card {:class "dark:bg-white"}
           [:div {:class "p-8 w-full flex items-center justify-center"}
            [:div
             [:img {:src (app.utils/maybe-local-url (:image-source filestore-definition))
                    :title (:name filestore-definition)
                    :class "max-w-full align-top object-contain"
                    :style {:height 80}}]]]

           (when can-edit?
             [:div {:class "absolute top-0 right-0 p-2"}
              [button {:variant  :empty-transparent
                       :data-cy  :filestore-edit-btn
                       :disabled (not can-edit?)
                       :on-click edit-filestore
                       :icon     PencilIcon
                       :size     :icon}
               ]])

           [:footer {:class "absolute bottom-0 right-0 left-0 p-2"}
            (if can-edit?
              [:div {:class "flex justify-between items-center"}


               (when-not valid?
                 [:div {}
                  [tooltip {:content (:error (:filestore-status/store-status filestore-status))}
                   [:div
                    [chip {:icon [:> AlertCircle {:size 14}]}
                     "Broken configuration"]]]])]

              [:div {:class "absolute bottom-0 right-0 left-0 p-2"}
               [gen/filestore-status]])]])))))

;; Page showing overall connections state
(defn overview []
  (reagent/create-class
   {:display-name
    "connections-overview"

    :constructor
    (fn []
      (dispatch-sync [:connections.api/fetch-user-connectors])
      (dispatch-sync [:connections.api/fetch-user-synchronizers])
      (dispatch-sync [:connections.events/load-data]))

    :render
    (fn []
      (let [sources                (re-posh/subscribe [:source/pull-many])
            connectors             (re-posh/subscribe [:connections.subs/all-enabled-connectors])
            sources-loading?       (re-posh/subscribe [:mold-http/loading? :connections.api/fetch-sources])
            synchronizers-loading? (re-posh/subscribe [:mold-http/loading? :connections.api/fetch-user-synchronizers])
            connectors-loading?    (re-posh/subscribe [:mold-http/loading? :connections.api/fetch-user-connectors])
            dark-mode              (re-posh/subscribe [:app/prop :app/dark-mode?])]
        [:div {:class "flex flex-col gap-6"
               :data-cy "connections-overview-screen"}
         [:div {:class "flex flex-col gap-2"}
          [typography/h2 "Warehouse and Filestore"]

          [:div {:class "flex gap-4"}
           [:div {:class "flex-1"} [warehouses]]
           [:div {:class "flex-1"} [filestores]]]]

         [:div {:class "flex flex-col gap-2"}
          [typography/h2 "Connected sources"]

          (if (or @sources-loading?
                  @connectors-loading?
                  @synchronizers-loading?)
            [:div {:class "grid gap-4 items-stretch"
                   :style {:grid-template-columns "repeat(auto-fill, minmax(250px, 1fr))"}}
             [skeleton {:height 115}]
             [skeleton {:height 115}]
             [skeleton {:height 115}]]

            (if-not (empty? @sources)
              [:div {:class "grid gap-4 items-stretch"
                     :style {:grid-template-columns "repeat(auto-fill, minmax(250px, 1fr))"}}
               (doall
                (map (fn [source-type]
                       (let [source-data (app.utils/find-connector-by-type-and-version
                                          @connectors source-type nil)]  ;; nil for latest version
                         ^{:key source-type}
                         [connection-preview
                          {:href         (href :extracts)
                           :name         (:name source-data)
                           :description  (:description source-data)
                           :image-source (:image-source source-data)
                           :dark-mode    @dark-mode}]))
                     (->> @sources
                          (group-by :source/source-type)
                          keys)))]

              [:div {:class "flex flex-col gap-2"}
               [typography/p "You haven't set up any extraction sources yet."]
               [:div
                [button {:variant  :primary
                         :on-click #(dispatch [:app.events/navigate :sources])
                         :data-cy  "connections-connect-source-btn"}
                 "Connect new Source"]]]))]]))}))
